import React, { FC } from 'react'
import { Typography, OutlinedInput, InputProps, ClickAwayListener, Box, IconButton } from '@mui/material'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { FormControl } from './textFieldCustomLabel.styles'
import theme from 'theme'
import { LibraryDetailsInfoTooltip } from 'ui/components/tooltip/LibraryDetailsTooltip'
import { LibraryTooltipActiveIcon, LibraryTooltipIcon } from 'ui/components/svg'

const muiTheme = createTheme({
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          '&:-webkit-autofill': {
            WebkitBackgroundClip: 'text !important',
            WebkitTextFillColor: theme.baseColors.greyscale[80],
            opacity: 1
          },
          '&::placeholder': {
            overflow: 'hidden',
            color: `${theme.baseColors.secondary[80]} !important`,
            WebkitTextFillColor: `${theme.baseColors.secondary[80]} !important`,
            textOverflow: 'ellipsis',
            fontSize: theme.typography.fontSize.xLarge,
            fontStyle: 'normal',
            fontWeight: theme.typography.fontWeight.normal,
            lineHeight: 'normal',
            opacity: '1 !important'
          }
        },
        root: {
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.colors.errorLight
          }
        }
      }
    }
  }
})

type Props = InputProps & {
  name: string
  label: string
  helperText?: string
  variant?: 'standard' | 'outlined' | 'filled'
  showInfoIcon?: boolean
  setEmptyInfoTooltipOpen?: any
  emptyInfoTooltipOpen?: any
  handleEmptyInfoClick?: any
  showAsterisk?: boolean
  counterLimit?: number
  value: string
}

const TextFieldCustomLabel: FC<Props> = ({
  label,
  variant,
  helperText,
  showInfoIcon,
  setEmptyInfoTooltipOpen,
  emptyInfoTooltipOpen,
  handleEmptyInfoClick,
  showAsterisk = true,
  counterLimit,
  value,
  ...rest
}) => {
  const hasError = Boolean(helperText)
  const isOverLimit = counterLimit ? value.length > counterLimit : false

  return showInfoIcon ? (
    <ThemeProvider theme={muiTheme}>
      <FormControl
        variant={variant || 'standard'}
        className="text-field-custom-label-form-control with-info-icon"
        iserrored={hasError ? 'true' : 'false'}
      >
        <Box className="form-control-inner">
          {label && (
            <Typography className="form-control-label">
              {label}
              {showAsterisk && <span>*</span>}
            </Typography>
          )}
          <OutlinedInput {...rest} value={value} error={hasError} />
          {helperText && (
            <Typography className={`helper-text ${hasError ? 'error-text' : ''}`}>{helperText}</Typography>
          )}
          {counterLimit && (
            <Box className="char-counter">
              <Typography className={`char-counter-text ${isOverLimit ? 'text-danger' : ''}`}>
                {value.length}/{counterLimit}
              </Typography>
            </Box>
          )}
        </Box>

        {showInfoIcon && (
          <ClickAwayListener onClickAway={() => setEmptyInfoTooltipOpen(false)}>
            <div className="info-icon-wrapper info-icon-all">
              <LibraryDetailsInfoTooltip
                PopperProps={{
                  disablePortal: true,
                  sx: {
                    marginTop: `-${theme.spacing.sm}px !important`
                  }
                }}
                open={emptyInfoTooltipOpen}
                disableFocusListener
                disableHoverListener
                disableTouchListener
                onClose={() => setEmptyInfoTooltipOpen(false)}
                title={
                  <>
                    <Box className="tooltip-wrapper">
                      <Typography className="tooltip-text">
                        The "All" severity category is a value representing the sum of your data regardless of its
                        designated severity.
                        <br />
                        <br />
                        For example, data without a designated severity would be included in this count.
                      </Typography>
                    </Box>
                  </>
                }
              >
                <IconButton className="tooltip-icon" onClick={handleEmptyInfoClick}>
                  {emptyInfoTooltipOpen ? <LibraryTooltipActiveIcon /> : <LibraryTooltipIcon />}
                </IconButton>
              </LibraryDetailsInfoTooltip>
            </div>
          </ClickAwayListener>
        )}
      </FormControl>
    </ThemeProvider>
  ) : (
    <ThemeProvider theme={muiTheme}>
      <FormControl
        variant={variant || 'standard'}
        className="text-field-custom-label-form-control"
        iserrored={hasError ? 'true' : 'false'}
      >
        {label && (
          <Typography className="form-control-label">
            {label}
            {showAsterisk && <span>*</span>}
          </Typography>
        )}
        <OutlinedInput {...rest} value={value} error={hasError} />
        {helperText && <Typography className={`helper-text ${hasError ? 'error-text' : ''}`}>{helperText}</Typography>}
        {typeof counterLimit !== 'undefined' && (
          <Box className="char-counter">
            <Typography className={`char-counter-text ${isOverLimit ? 'text-danger' : ''}`}>
              {value.length}/{counterLimit}
            </Typography>
          </Box>
        )}
      </FormControl>
    </ThemeProvider>
  )
}

export default TextFieldCustomLabel
